import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@repo/ui";
import { PrinterIcon } from "lucide-react";
import { useEffect, useState } from "react";

import {
  useMarkCouponsPrinted,
  useRegistrationDetail,
  useUpdateRegistration,
  useUpdateRegistrationGift,
} from "@/lib/queries/use-registration";

export default function RegistrationModal({
  open,
  onOpenChange,
  itemId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  itemId: number | null;
}) {
  const { data, isLoading } = useRegistrationDetail(itemId ?? undefined);
  const updateGift = useUpdateRegistrationGift();
  const updateRegistration = useUpdateRegistration();
  const markCouponsPrinted = useMarkCouponsPrinted();
  const [saving, setSaving] = useState(false);

  const current = data?.item;

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    phone: "",
    email: "",
    address: "",
    age: "",
    interests: "",
    interest_other: "",
    banks: "",
    property_bought: "",
    value_amount: "",
    developer: "",
  });

  // Initialize form when data loads
  useEffect(() => {
    if (current) {
      setFormData({
        name: current.name || "",
        surname: current.surname || "",
        phone: current.phone || "",
        email: current.email || "",
        address: current.address || "",
        age: current.age?.toString() || "",
        interests: current.interests.join(", "),
        interest_other: current.interest_other || "",
        banks: current.banks.join(", "),
        property_bought: current.property_bought || "",
        value_amount: current.value_amount || "",
        developer: current.developer || "",
      });
    }
  }, [current]);

  const handleSave = async () => {
    if (!current) return;

    setSaving(true);
    try {
      await updateRegistration.mutateAsync({
        id: current.id,
        name: formData.name,
        surname: formData.surname,
        phone: formData.phone,
        email: formData.email,
        address: formData.address || null,
        age: formData.age ? Number(formData.age) : null,
        interests: formData.interests
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        interestOther: formData.interest_other || null,
        banks: formData.banks
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        property_bought: formData.property_bought || null,
        value_amount: formData.value_amount || null,
        developer: formData.developer || null,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Registration #{itemId ?? "..."}</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="text-center py-8">Loading...</div>
        ) : current ? (
          <div className="space-y-4 w-full overflow-hidden">
            <div className="flex justify-between gap-2">
              <Button onClick={handleSave} disabled={saving || updateRegistration.isPending}>
                {saving || updateRegistration.isPending ? "Saving..." : "Save Changes"}
              </Button>
              <Button
                variant="outline"
                disabled={!current.coupon_ready_at}
                onClick={() => {
                  if (current.id) {
                    markCouponsPrinted.mutate({ id: current.id });
                  }
                  window.open(`/admin/print-coupon/${current.id}`, "_blank");
                }}
              >
                <PrinterIcon />
                Print Coupons
              </Button>
            </div>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">Created</TableCell>
                  <TableCell>{new Date(current.created_at).toLocaleString()}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Name</TableCell>
                  <TableCell>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Surname</TableCell>
                  <TableCell>
                    <Input
                      value={formData.surname}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, surname: e.target.value }))
                      }
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Phone</TableCell>
                  <TableCell>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Email</TableCell>
                  <TableCell>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Address</TableCell>
                  <TableCell>
                    <Input
                      value={formData.address}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, address: e.target.value }))
                      }
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Age</TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      value={formData.age}
                      onChange={(e) => setFormData((prev) => ({ ...prev, age: e.target.value }))}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Interests</TableCell>
                  <TableCell>
                    <Input
                      value={formData.interests}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, interests: e.target.value }))
                      }
                      placeholder="Comma-separated values"
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Other</TableCell>
                  <TableCell>
                    <Input
                      value={formData.interest_other}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, interest_other: e.target.value }))
                      }
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Banks</TableCell>
                  <TableCell>
                    <Input
                      value={formData.banks}
                      onChange={(e) => setFormData((prev) => ({ ...prev, banks: e.target.value }))}
                      placeholder="Comma-separated values"
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Property Bought</TableCell>
                  <TableCell>
                    <Input
                      value={formData.property_bought}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, property_bought: e.target.value }))
                      }
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Value Amount</TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      value={formData.value_amount}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, value_amount: e.target.value }))
                      }
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Developer</TableCell>
                  <TableCell>
                    <Input
                      value={formData.developer}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, developer: e.target.value }))
                      }
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Coupon Ready</TableCell>
                  <TableCell>
                    {current.coupon_ready_at
                      ? new Date(current.coupon_ready_at).toLocaleString()
                      : "No"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Coupons Printed</TableCell>
                  <TableCell>
                    {current.coupons_printed_at
                      ? new Date(current.coupons_printed_at).toLocaleString()
                      : "Not printed"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Received gift</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-4">
                      <span>
                        {current.received_gift_at
                          ? new Date(current.received_gift_at).toLocaleString()
                          : "No"}
                      </span>
                      <Button
                        size="sm"
                        variant={current.received_gift_at ? "secondary" : "default"}
                        disabled={saving || updateGift.isPending}
                        onClick={async () => {
                          setSaving(true);
                          try {
                            await updateGift.mutateAsync({
                              id: current.id,
                              received_gift: !current.received_gift_at,
                            });
                          } finally {
                            setSaving(false);
                          }
                        }}
                      >
                        {saving || updateGift.isPending
                          ? "Saving..."
                          : current.received_gift_at
                            ? "Mark as not received"
                            : "Mark as received"}
                      </Button>
                    </span>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8">No data found</div>
        )}
      </DialogContent>
    </Dialog>
  );
}
